if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.initQtySelector();
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              // Partial add: some items were added despite the availability limit —
              // fetch the live cart and open the drawer so the customer sees what was added.
              const isPartialAdd =
                response.status === 422 &&
                typeof response.description === 'string' &&
                /added/i.test(response.description) &&
                this.cart;

              if (isPartialAdd) {
                const sectionIds = this.cart.getSectionsToRender().map((s) => s.id);
                fetch(`/cart?sections=${sectionIds.join(',')}`)
                  .then((r) => r.json())
                  .then((sections) => {
                    this.cart.renderContents({ sections });
                  })
                  .catch((e) => console.error(e));
                return;
              }

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      initQtySelector() {
        const wrapper = this.querySelector('.card-qty-selector');
        if (!wrapper) return;

        const caseSize = parseInt(wrapper.dataset.caseSize, 10) || 1;
        const visibleInput = wrapper.querySelector('.card-qty-selector__input');
        const hiddenQty = wrapper.querySelector('.card-qty-selector__qty');
        const casesLabel = wrapper.querySelector('.card-qty-selector__cases');
        const unitsLabel = wrapper.querySelector('.card-qty-selector__units');

        const update = (cases) => {
          const n = Math.max(1, cases);
          visibleInput.value = n;
          hiddenQty.value = n * caseSize;
          casesLabel.textContent = n;
          unitsLabel.textContent = n * caseSize;
        };

        wrapper.querySelector('.card-qty-selector__btn--minus').addEventListener('click', () => {
          update((parseInt(visibleInput.value, 10) || 1) - 1);
        });

        wrapper.querySelector('.card-qty-selector__btn--plus').addEventListener('click', () => {
          update((parseInt(visibleInput.value, 10) || 1) + 1);
        });

        visibleInput.addEventListener('change', () => {
          update(parseInt(visibleInput.value, 10) || 1);
        });
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

if (!customElements.get('pdp-case-selector')) {
  customElements.define(
    'pdp-case-selector',
    class PdpCaseSelector extends HTMLElement {
      connectedCallback() {
        this.caseSize = parseInt(this.dataset.caseSize, 10) || 1;
        this.unitPrice = parseInt(this.dataset.price, 10) || 0;
        this.discount = parseFloat(this.dataset.discount) || 0;
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

        this.casesInput = this.querySelector('.pdp-case-selector__input');
        this.unitsLabel = this.querySelector('.pdp-case-selector__units-label');
        this.priceMain = this.querySelector('.pdp-case-selector__price-main');
        this.priceCompare = this.querySelector('.pdp-case-selector__price-compare');
        this.formQty = this.parentElement.querySelector('.pdp-case-selector__form-qty');

        this.querySelector('.pdp-case-selector__btn--minus').addEventListener('click', () =>
          this.setCases((parseInt(this.casesInput.value, 10) || 1) - 1)
        );
        this.querySelector('.pdp-case-selector__btn--plus').addEventListener('click', () =>
          this.setCases((parseInt(this.casesInput.value, 10) || 1) + 1)
        );
        this.querySelectorAll('.pdp-case-selector__preset').forEach((btn) =>
          btn.addEventListener('click', () => this.setCases(parseInt(btn.dataset.cases, 10)))
        );
        this.casesInput.addEventListener('change', () =>
          this.setCases(parseInt(this.casesInput.value, 10) || 1)
        );

        this.setCases(1);
      }

      setCases(n) {
        const cases = Math.max(1, n || 1);
        this.casesInput.value = cases;

        const units = cases * this.caseSize;
        const totalCents = units * this.unitPrice;

        this.unitsLabel.textContent = `${units} units`;

        if (this.discount > 0) {
          const discounted = Math.round(totalCents * (1 - this.discount / 100));
          this.priceMain.textContent = this.formatMoney(discounted);
          this.priceCompare.textContent = this.formatMoney(totalCents);
          this.priceCompare.classList.remove('hidden');
        } else {
          this.priceMain.textContent = this.formatMoney(totalCents);
          this.priceCompare.classList.add('hidden');
        }

        if (this.formQty) this.formQty.value = units;
      }

      formatMoney(cents) {
        const amount = (cents / 100).toFixed(2);
        return this.moneyFormat
          .replace('{{amount}}', amount)
          .replace('{{amount_no_decimals}}', Math.round(cents / 100))
          .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
          .replace('{{amount_no_decimals_with_comma_separator}}', String(Math.round(cents / 100)).replace('.', ','));
      }
    }
  );
}
